import { graphql } from "@/gql";
import { ResultOf } from "@graphql-typed-document-node/core";

export type BookingRecord = ResultOf<typeof bookingRecordQuery>["booking"];
export const bookingRecordQuery = graphql(`
  query BookingRecordQuery($id: ID!) {
    booking(id: $id) {
      id
      uuid
      created_at
      updated_at
      reference
      email
      phone
      name
      number_of_travellers
      address
      gender
      supplier_reference
      customer_note
      date_of_birth
      status
      customer_url
      depart_at
      deliver_at
      confirmed_at
      hire_unit_type
      original_relocation_id
      currency
      distance_allowed
      full_price_units
      discounted_units
      preferred_ferry_date
      measurement

      user {
        id
        name
        profilePicture {
          ...MediaFields
        }
      }

      createdBy {
        id
        name
        profilePicture {
          ...MediaFields
        }
      }

      relocation {
        id
        type
        reference
        booking_fee_amount
        charge_per_move_amount
        holding_deposit_amount
        is_ferry_required
        status
        count
        customer_url
        bookedBookingsCount
      }

      supplier {
        id
        name
        ...SupplierIconFields
        is_tax_included
        billing_currency
        billing_currency
        tax_type
        collect_address_on_bookings
        collect_gender_on_bookings
        debit_card_accepted

        minimum_age
        maximum_age
        age_surcharge_type
        age_surcharge_amount
        maximum_no_surcharge_age
        minimum_no_surcharge_age
        has_age_surcharge

        imoova_refunds_fuel
        fuel_refund_fee
        imoova_refunds_ferry
        ferry_refund_fee

        relist_on_cancellation

        notificationSettings {
          id
          event
        }

        billingEntity {
          ...ContactRecordFields
        }
        managedBy {
          ...OrganisationFields
        }
      }

      vehicle {
        id
        name
        heroImage
        type
        transmission
        seatbelts
        sleeps
        is_self_contained
        is_pet_friendly
        has_heating
        has_kitchen
        has_shower_and_toilet
        child_seat_anchor_points
        code
        approximate_age
        fuel
        model
        brand
        updated_at
        created_at

        images {
          ...MediaFields
        }
      }

      departureOffice {
        id
        name
        code
        address {
          ...AddressFields
        }
        city {
          id
          name
          region
          lat
          lng
        }
      }

      deliveryOffice {
        id
        name
        code
        address {
          ...AddressFields
        }
        city {
          id
          name
          region
          lat
          lng
        }
        billingEntity {
          ...ContactRecordFields
        }
      }

      customerPayments {
        ...PaymentFields
      }

      supplierPayments {
        ...PaymentFields
      }

      availablePurchasableExtras {
        id
        name
        unit_price
        units_purchasable
      }

      purchasableExtras {
        id
        quantity
        supplierPurchasableExtra {
          id
          name
          description
          unit_price
          units_purchasable
        }
      }

      relocationLine {
        id
        reference
        created_at
      }

      userRating {
        isVerified
        isVerifiedEmail
        isVerifiedPhone
        isBlocked
        completedRelocationCount
      }

      potentialDuplicates {
        id
        reference
        deliver_at
        depart_at
        relocation {
          departureCity {
            name
          }
          deliveryCity {
            name
          }
        }
      }

      charges {
        id
        type
        quantity
        unit_price
        platform_unit_price_collectable
        supplier_unit_price_collectable
        amount
        platform_amount_collectable
        supplier_amount_collectable
        booking_purchasable_extra_id
        purchasableExtra {
          id
          quantity
          supplierPurchasableExtra {
            id
            name
          }
        }
        inclusion {
          id
          description
          value
          type
        }
      }

      charge_summary {
        pay_now_booking_fee_amount
        pay_now_cost_amount
        pay_now_daily_rate_deposit_amount
        pay_now_holding_deposit_amount
        pay_now_total_amount

        pay_later_additional_charges_amount
        pay_later_daily_rate_amount
        pay_later_purchasable_extra_amount
        pay_later_relocation_rate_amount
        pay_later_total_amount
        pay_later_subtotal_amount

        subtotal_amount
        refund_amount
        total_amount

        supplier_refund_amount
        platform_refund_amount
      }

      inclusions {
        ...BookingInclusionFields
      }

      supplierInsuranceOption {
        id
        name
        bond_amount
        standard_liability_amount
        liability_reduction_options
        created_at
        updated_at
      }
    }
  }
`);

graphql(`
  fragment BookingInclusionFields on BookingInclusion {
    id
    type
    description
    value
    has_free_tank
    created_at
  }
`);
